// services/adminApi.ts — HTTP client for the bearer-authed /api/admin/*
// endpoints the web app calls directly (vs the socket-only admin portal
// at admin.emberhollow.ai).
//
// Currently a single call:
//   adminSetSpice(storyId, level) → POST /api/admin/spice
//
// The endpoint silently ignores non-admin callers (204 with no body) and
// 204s on success too — both paths look identical from the client's POV
// so it's effectively fire-and-forget. The UI gates the slider on
// user.isAdmin before ever calling this; the server check is the
// belt to that braces.

import { authedFetch } from "../auth/tokens";

/**
 * Set the spice level on the named story. Returns nothing on success;
 * the caller optimistically applies the change locally on resolve.
 *
 * On 204 (real success OR silently ignored — indistinguishable by design):
 *   resolve with no value.
 * On 400 (malformed input — shouldn't happen from a well-formed slider):
 *   warn-log; resolve. The UI doesn't need to differentiate.
 * On 401: authedFetch handles the refresh-retry path; if that fails it
 *   forces a re-auth and we never get here.
 * On 5xx / network error: throw. The caller's catch swallows it (the
 *   admin slider deliberately doesn't surface UI feedback for this).
 */
export async function adminSetSpice(
  storyId: string,
  level: number,
): Promise<void> {
  const res = await authedFetch("/api/admin/spice", {
    method: "POST",
    body: JSON.stringify({ storyId, level }),
  });
  if (res.status === 204) return;
  if (res.status === 400) {
    console.warn("[admin:spice] 400 from /api/admin/spice — malformed input");
    return;
  }
  // Anything else (500 etc.) — throw so the caller can decide whether
  // to surface it. The current call site swallows it.
  if (!res.ok) {
    throw new Error(`/api/admin/spice returned ${res.status}`);
  }
}
